import {
  Component,
  ElementRef,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  DestroyRef,
  HostListener,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CropRect, FlatRecorder } from './flat-recorder';
import { RecordingFormat } from '../recorder-core';

const RECORDING_FORMATS: ReadonlyArray<RecordingFormat> = ['16:9', '9:16', '4:5', '1:1'];

interface Point {
  x: number;
  y: number;
}

interface DrawnStroke {
  points: Point[];
  completedAt: number | null;
}

@Component({
  selector: 'app-flat-video-player',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './flat-video-player.component.html',
  styleUrls: ['./flat-video-player.component.scss'],
})
export class FlatVideoPlayerComponent {
  videoUrl = input.required<string>();
  recordingStartTime = input.required<string | Date>();
  startTimeSec = input<number>(0);
  fps = input<number | null>(null);
  timeUpdate = output<number>();

  playerWrapperRef = viewChild.required<ElementRef<HTMLDivElement>>('playerWrapper');
  videoViewportRef = viewChild.required<ElementRef<HTMLDivElement>>('videoViewport');
  videoRef = viewChild.required<ElementRef<HTMLVideoElement>>('video');
  seekBarRef = viewChild.required<ElementRef<HTMLInputElement>>('seekBar');
  canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('drawingCanvas');

  isPlaying = signal(false);
  progressPercentage = signal(0);
  currentTimeSec = signal(0);
  totalDuration = signal(0);
  isUserActive = signal(true);
  isFullscreen = signal(false);
  volume = signal(0);
  isMuted = signal(true);

  zoom = signal(1);
  tx = signal(0);
  ty = signal(0);
  videoNaturalW = signal(0);
  videoNaturalH = signal(0);
  viewportW = signal(0);
  viewportH = signal(0);

  // --- Recording State ---
  readonly recordingFormats = RECORDING_FORMATS;
  isRecording = signal(false);
  showRecordMenu = signal(false);
  recordingFormat = signal<RecordingFormat | null>(null);
  recordingElapsedSec = signal(0);
  private recorder: FlatRecorder | null = null;
  private recordingTickInterval: any = null;
  // Wall-clock timestamp (Date.now()) when the current recording started.
  private recordingStartedAtMs = 0;
  private recordingRafHandle: number | null = null;
  private recordingFrameDriverActive = false;
  // Guards the await gap inside startRecording (see the comment there).
  private recordingStarting = false;

  isDrawMode = signal(false);
  hasDrawings = signal(false);
  isScrubbing = signal(false);
  showPreview = signal(false);
  previewLeftPx = signal(0);
  previewTime = signal<Date | null>(null);
  seekFeedbackDirection = signal<'backward' | 'forward' | null>(null);
  seekFeedbackSeconds = signal(0);

  // Preview thumbnail
  private readonly PREVIEW_PREF_KEY = 'pano.scrubPreviewEnabled';
  isCoarsePointer = (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  previewEnabled = signal(this.isCoarsePointer ? false : this.loadPreviewPref());
  showSettings = signal(false);
  previewVideoRef = viewChild<ElementRef<HTMLVideoElement>>('previewVideo');
  private previewSeekTimeout: any = null;
  private pendingPreviewSec: number | null = null;

  private loadPreviewPref(): boolean {
    try {
      const v = localStorage.getItem(this.PREVIEW_PREF_KEY);
      if (v === null) return true;
      return v === 'true';
    } catch {
      return true;
    }
  }
  private savePreviewPref(value: boolean) {
    try { localStorage.setItem(this.PREVIEW_PREF_KEY, String(value)); } catch {}
  }

  transformStyle = computed(() => {
    const baseScale = this.computeBaseScale();
    const scale = baseScale * this.zoom();
    return `translate(-50%, -50%) translate(${this.tx()}px, ${this.ty()}px) scale(${scale})`;
  });

  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private strokes: DrawnStroke[] = [];
  private currentStroke: DrawnStroke | null = null;
  private rafId: number | null = null;

  private readonly FADE_DELAY_MS = 4000;
  private readonly FADE_DURATION_MS = 2000;
  private readonly MAX_ZOOM = 4;
  private readonly MIN_ZOOM = 1;
  private readonly TAP_THRESHOLD_MS = 200;

  private playbackModifier: 'none' | 'fast' | 'slow' | 'reverse' = 'none';
  private rewindInterval: any = null;
  private wasPlayingBeforeRewind = false;
  private arrowHoldTimeout: any = null;
  private heldArrowKey: 'ArrowLeft' | 'ArrowRight' | null = null;
  private actionStartTime = 0;
  private wasPlayingBeforeAction = false;
  private seekFeedbackTimeout: any = null;
  private touchTapResetTimeout: any = null;
  private touchTapCount = 0;
  private lastTouchTapAt = 0;
  private lastTouchTapSide: 'backward' | 'forward' | null = null;
  private touchSeekPointers = new Map<number, {
    startX: number;
    startY: number;
    startedAt: number;
    moved: boolean;
    eligible: boolean;
  }>();

  private destroyRef = inject(DestroyRef);
  private userActivityTimeout: any;
  private fullscreenHandler = () => this.isFullscreen.set(!!document.fullscreenElement);

  // Pointer state for pan / pinch
  private activePointers = new Map<number, { x: number; y: number }>();
  private panLastX = 0;
  private panLastY = 0;
  private isPanning = false;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  private pinchCenter: Point = { x: 0, y: 0 };

  currentRealTime = computed(() => this.getRealTimeFromSeconds(this.currentTimeSec()));
  endTime = computed(() => this.getRealTimeFromSeconds(this.totalDuration()));

  constructor() {
    afterNextRender(() => {
      this.initVideo();
      this.initCanvas();
      this.trackUserActivity();
      this.listenToFullscreenChange();
      this.observeResize();
    });

    this.destroyRef.onDestroy(() => {
      this.abortRecording();
      this.stopRewind();
      clearTimeout(this.arrowHoldTimeout);
      clearTimeout(this.userActivityTimeout);
      clearTimeout(this.seekFeedbackTimeout);
      clearTimeout(this.touchTapResetTimeout);
      if (this.previewSeekTimeout) clearTimeout(this.previewSeekTimeout);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      document.removeEventListener('fullscreenchange', this.fullscreenHandler);
    });

    // Re-clamp transform when zoom/dims change
    effect(() => {
      this.zoom();
      this.videoNaturalW();
      this.videoNaturalH();
      this.viewportW();
      this.viewportH();
      this.clampTranslation();
    });

    // Reset on URL change
    effect(() => {
      this.videoUrl();
      // Read recording state non-reactively: toggling isRecording must NOT
      // cause this effect to re-run and reset the user's zoom mid-take.
      if (untracked(() => this.isRecording())) {
        // Angle switched mid-recording — finalize and download the clip
        // rather than dropping it.
        void this.stopRecording();
      }
      this.zoom.set(1);
      this.tx.set(0);
      this.ty.set(0);
    });
  }

  // =========================================================
  // VIDEO INIT
  // =========================================================

  private initVideo() {
    const video = this.videoRef().nativeElement;
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;

    video.addEventListener('loadedmetadata', () => {
      this.videoNaturalW.set(video.videoWidth);
      this.videoNaturalH.set(video.videoHeight);
      this.totalDuration.set(video.duration || 0);
      this.volume.set(video.volume);
      this.isMuted.set(video.muted);
      const seed = this.startTimeSec();
      if (seed > 0 && seed < (video.duration || Infinity)) {
        video.currentTime = seed;
      }
    });
    video.addEventListener('play', () => this.isPlaying.set(true));
    video.addEventListener('pause', () => this.isPlaying.set(false));
    video.addEventListener('timeupdate', () => {
      const t = video.currentTime;
      const d = video.duration || 0;
      this.timeUpdate.emit(t);
      if (!this.isScrubbing()) {
        this.currentTimeSec.set(t);
        this.totalDuration.set(d);
        this.progressPercentage.set(d > 0 ? (t / d) * 100 : 0);
      }
    });
    video.addEventListener('volumechange', () => {
      this.volume.set(video.volume);
      this.isMuted.set(video.muted || video.volume === 0);
    });
  }

  /**
   * Observes the viewport box, NOT the wrapper. While recording, the viewport
   * shrinks to the target aspect ratio, and every piece of pan/zoom math keys
   * off these dimensions: computeBaseScale() then cover-fits the video to the
   * locked box, clampTranslation() keeps black edges out of it, and the crop
   * rect handed to the recorder maps to it exactly.
   */
  private observeResize() {
    const viewport = this.videoViewportRef().nativeElement;
    const update = () => {
      this.viewportW.set(viewport.clientWidth);
      this.viewportH.set(viewport.clientHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(viewport);
  }

  // =========================================================
  // PAN / ZOOM MATH
  // =========================================================

  private computeBaseScale(): number {
    const vw = this.videoNaturalW();
    const vh = this.videoNaturalH();
    const pw = this.viewportW();
    const ph = this.viewportH();
    if (!vw || !vh || !pw || !ph) return 1;
    return Math.max(pw / vw, ph / vh);
  }

  private maxTranslation(): { x: number; y: number } {
    const baseScale = this.computeBaseScale();
    const scale = baseScale * this.zoom();
    const scaledW = this.videoNaturalW() * scale;
    const scaledH = this.videoNaturalH() * scale;
    return {
      x: Math.max(0, (scaledW - this.viewportW()) / 2),
      y: Math.max(0, (scaledH - this.viewportH()) / 2),
    };
  }

  private clampTranslation() {
    const { x: maxX, y: maxY } = this.maxTranslation();
    this.tx.update(v => Math.max(-maxX, Math.min(maxX, v)));
    this.ty.update(v => Math.max(-maxY, Math.min(maxY, v)));
  }

  // =========================================================
  // POINTER (PAN + PINCH)
  // =========================================================

  onWrapperPointerDown(event: PointerEvent) {
    this.onSeekPointerDown(event);
    if (this.isDrawMode()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.controls-overlay')) return;
    const wrapper = this.playerWrapperRef().nativeElement;
    wrapper.setPointerCapture(event.pointerId);
    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.activePointers.size === 1) {
      this.isPanning = true;
      this.panLastX = event.clientX;
      this.panLastY = event.clientY;
    } else if (this.activePointers.size === 2) {
      this.isPanning = false;
      const pts = Array.from(this.activePointers.values());
      this.pinchStartDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      this.pinchStartZoom = this.zoom();
      // Viewport-local, not wrapper-local: zoomAroundPoint measures the offset
      // from viewportW()/2, so the two must share an origin.
      const rect = this.videoViewportRef().nativeElement.getBoundingClientRect();
      this.pinchCenter = {
        x: (pts[0].x + pts[1].x) / 2 - rect.left,
        y: (pts[0].y + pts[1].y) / 2 - rect.top,
      };
    }
  }

  onWrapperPointerMove(event: PointerEvent) {
    this.onSeekPointerMove(event);
    if (this.isDrawMode()) return;
    if (!this.activePointers.has(event.pointerId)) return;
    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.activePointers.size === 1 && this.isPanning) {
      const dx = event.clientX - this.panLastX;
      const dy = event.clientY - this.panLastY;
      this.panLastX = event.clientX;
      this.panLastY = event.clientY;
      this.tx.update(v => v + dx);
      this.ty.update(v => v + dy);
      this.clampTranslation();
    } else if (this.activePointers.size === 2) {
      const pts = Array.from(this.activePointers.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (this.pinchStartDist > 0) {
        const newZoom = Math.max(
          this.MIN_ZOOM,
          Math.min(this.MAX_ZOOM, this.pinchStartZoom * (dist / this.pinchStartDist)),
        );
        this.zoomAroundPoint(newZoom, this.pinchCenter);
      }
    }
  }

  onWrapperPointerUp(event: PointerEvent, cancelled = false) {
    if (cancelled) this.onSeekPointerCancel(event);
    else this.onSeekPointerUp(event);
    this.activePointers.delete(event.pointerId);
    if (this.activePointers.size < 2) {
      this.pinchStartDist = 0;
    }
    if (this.activePointers.size === 1) {
      const remaining = Array.from(this.activePointers.values())[0];
      this.panLastX = remaining.x;
      this.panLastY = remaining.y;
      this.isPanning = true;
    } else if (this.activePointers.size === 0) {
      this.isPanning = false;
    }
  }

  onWrapperWheel(event: WheelEvent) {
    if (this.isDrawMode()) return;
    event.preventDefault();
    const rect = this.videoViewportRef().nativeElement.getBoundingClientRect();
    const center = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
    const newZoom = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, this.zoom() * factor));
    this.zoomAroundPoint(newZoom, center);
  }

  private zoomAroundPoint(newZoom: number, center: Point) {
    // center is in wrapper-local coords. Image origin is wrapper center.
    const cx = this.viewportW() / 2;
    const cy = this.viewportH() / 2;
    const offsetX = center.x - cx;
    const offsetY = center.y - cy;
    const oldZoom = this.zoom();
    if (oldZoom === 0) return;
    const ratio = newZoom / oldZoom;
    // Keep the image point under cursor fixed:
    // newTx = offsetX - (offsetX - tx) * ratio
    this.tx.update(v => offsetX - (offsetX - v) * ratio);
    this.ty.update(v => offsetY - (offsetY - v) * ratio);
    this.zoom.set(newZoom);
    this.clampTranslation();
  }

  // =========================================================
  // CANVAS & DRAWING
  // =========================================================

  private initCanvas() {
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    this.resizeCanvas();
    const ro = new ResizeObserver(() => this.resizeCanvas());
    ro.observe(this.videoViewportRef().nativeElement);
    this.renderLoop();
  }

  private resizeCanvas() {
    const canvas = this.canvasRef().nativeElement;
    // Sized off the viewport so strokes stay aligned with the video once the
    // viewport letterboxes for recording.
    const viewport = this.videoViewportRef().nativeElement;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = viewport.clientWidth * dpr;
    canvas.height = viewport.clientHeight * dpr;
    if (this.ctx) {
      this.ctx.scale(dpr, dpr);
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.lineWidth = 8;
    }
  }

  toggleDrawMode() { this.isDrawMode.update(v => !v); }

  clearDrawings() {
    this.strokes = [];
    this.currentStroke = null;
    this.hasDrawings.set(false);
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    if (!this.isDrawMode()) return;
    this.isDrawing = true;
    const pos = this.getEventPos(event);
    this.currentStroke = { points: [pos], completedAt: null };
    this.strokes.push(this.currentStroke);
    this.hasDrawings.set(true);
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing || !this.isDrawMode() || !this.currentStroke) return;
    event.preventDefault();
    const pos = this.getEventPos(event);
    this.currentStroke.points.push(pos);
  }

  stopDrawing() {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    if (this.currentStroke) {
      this.currentStroke.completedAt = Date.now();
      this.currentStroke = null;
    }
  }

  private getEventPos(event: MouseEvent | TouchEvent): Point {
    const canvas = this.canvasRef().nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if (window.TouchEvent && event instanceof TouchEvent) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = (event as MouseEvent).clientX;
      clientY = (event as MouseEvent).clientY;
    }
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private renderLoop() {
    this.updateAndDraw();
    this.rafId = requestAnimationFrame(() => this.renderLoop());
  }

  private updateAndDraw() {
    if (!this.ctx) return;
    const canvas = this.canvasRef().nativeElement;
    const width = canvas.width / (window.devicePixelRatio || 1);
    const height = canvas.height / (window.devicePixelRatio || 1);
    const now = Date.now();

    this.ctx.clearRect(0, 0, width, height);

    this.strokes = this.strokes.filter(stroke => {
      if (!stroke.completedAt) return true;
      const age = now - stroke.completedAt;
      return age < (this.FADE_DELAY_MS + this.FADE_DURATION_MS);
    });

    this.hasDrawings.set(this.strokes.length > 0);

    this.strokes.forEach(stroke => {
      if (!this.ctx) return;
      let alpha = 1;
      if (stroke.completedAt) {
        const age = now - stroke.completedAt;
        if (age > this.FADE_DELAY_MS) {
          const progress = (age - this.FADE_DELAY_MS) / this.FADE_DURATION_MS;
          alpha = Math.max(0, 1 - progress);
        }
      }
      this.ctx.beginPath();
      this.ctx.strokeStyle = `rgba(250, 204, 21, ${alpha})`;
      if (stroke.points.length > 0) {
        this.ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        for (let i = 1; i < stroke.points.length; i++) {
          this.ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
      }
      this.ctx.stroke();
    });
  }

  // =========================================================
  // KEYBOARD
  // =========================================================

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      if (!this.isDrawMode()) this.isDrawMode.set(true);
      return;
    }

    const video = this.videoRef()?.nativeElement;
    if (!video) return;

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      if (this.isArrowKeyInputTarget(event.target)) return;
      event.preventDefault();
      if (event.repeat || this.playbackModifier !== 'none') return;
      this.scheduleArrowHold(event.key);
      return;
    }

    if (this.isKeyboardInputTarget(event.target)) return;
    if (event.repeat) return;
    if (this.playbackModifier !== 'none') return;

    switch (event.key) {
      case 'ArrowDown': this.startMomentaryAction('slow', 0.5); break;
      case ' ': this.togglePlay(); break;
    }
  }

  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.isDrawMode.set(false);
      this.stopDrawing();
      return;
    }

    const video = this.videoRef()?.nativeElement;
    if (!video) return;
    const isPendingArrowTap = event.key === this.heldArrowKey && this.playbackModifier === 'none';
    if (event.key === this.heldArrowKey) {
      clearTimeout(this.arrowHoldTimeout);
      this.heldArrowKey = null;
    }
    if (isPendingArrowTap && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      this.seekBy(event.key === 'ArrowRight' ? 5 : -5, 5, true);
      return;
    }
    switch (event.key) {
      case 'ArrowRight': if (this.playbackModifier === 'fast') this.endHeldFastForward(); break;
      case 'ArrowLeft': if (this.playbackModifier === 'reverse') this.endHeldRewind(); break;
      case 'ArrowDown': if (this.playbackModifier === 'slow') this.endMomentaryAction(); break;
    }
  }

  private isKeyboardInputTarget(target: EventTarget | null): boolean {
    return target instanceof HTMLElement &&
      (target.isContentEditable || target.matches('input, textarea, select, button'));
  }

  private isArrowKeyInputTarget(target: EventTarget | null): boolean {
    return target instanceof HTMLElement &&
      (target.isContentEditable || target.matches('input, textarea, select'));
  }

  private scheduleArrowHold(key: 'ArrowLeft' | 'ArrowRight') {
    clearTimeout(this.arrowHoldTimeout);
    this.heldArrowKey = key;
    this.arrowHoldTimeout = setTimeout(() => {
      if (this.heldArrowKey !== key || this.playbackModifier !== 'none') return;
      if (key === 'ArrowRight') this.startHeldFastForward();
      else this.startHeldRewind();
    }, 300);
  }

  private seekBy(deltaSeconds: number, feedbackSeconds: number, accumulateFeedback = false) {
    const video = this.videoRef()?.nativeElement;
    if (!video) return;
    const duration = Number.isFinite(video.duration) ? video.duration : this.totalDuration();
    const nextTime = Math.max(0, Math.min(duration > 0 ? duration : Infinity, video.currentTime + deltaSeconds));
    video.currentTime = nextTime;
    this.currentTimeSec.set(nextTime);
    this.progressPercentage.set(duration > 0 ? (nextTime / duration) * 100 : 0);
    this.showSeekFeedback(deltaSeconds < 0 ? 'backward' : 'forward', feedbackSeconds, accumulateFeedback);
  }

  private showSeekFeedback(direction: 'backward' | 'forward', seconds: number, accumulate: boolean) {
    const displayedSeconds = accumulate && this.seekFeedbackDirection() === direction
      ? this.seekFeedbackSeconds() + seconds
      : seconds;
    this.seekFeedbackDirection.set(direction);
    this.seekFeedbackSeconds.set(displayedSeconds);
    clearTimeout(this.seekFeedbackTimeout);
    this.seekFeedbackTimeout = setTimeout(() => {
      this.seekFeedbackDirection.set(null);
      this.seekFeedbackSeconds.set(0);
    }, 1600);
  }

  private onSeekPointerDown(event: PointerEvent) {
    if (event.pointerType !== 'touch') return;
    const target = event.target as HTMLElement | null;
    const eligible = !this.isDrawMode() && !target?.closest('.controls-overlay');
    this.touchSeekPointers.set(event.pointerId, {
      startX: event.clientX,
      startY: event.clientY,
      startedAt: Date.now(),
      moved: false,
      eligible,
    });
    if (this.touchSeekPointers.size > 1) {
      this.touchSeekPointers.forEach(pointer => pointer.eligible = false);
    }
  }

  private onSeekPointerMove(event: PointerEvent) {
    const pointer = this.touchSeekPointers.get(event.pointerId);
    if (!pointer) return;
    if (Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) > 18) {
      pointer.moved = true;
    }
  }

  private onSeekPointerUp(event: PointerEvent) {
    const pointer = this.touchSeekPointers.get(event.pointerId);
    this.touchSeekPointers.delete(event.pointerId);
    if (!pointer?.eligible || pointer.moved || Date.now() - pointer.startedAt > 350) return;

    const rect = this.playerWrapperRef().nativeElement.getBoundingClientRect();
    const relativeX = (event.clientX - rect.left) / rect.width;
    const side = relativeX < 0.4 ? 'backward' : relativeX > 0.6 ? 'forward' : null;
    if (!side) {
      this.resetTouchTapSequence();
      return;
    }

    const now = Date.now();
    const maxGap = this.touchTapCount >= 2 ? 650 : 450;
    if (side === this.lastTouchTapSide && now - this.lastTouchTapAt <= maxGap) {
      this.touchTapCount += 1;
    } else {
      this.touchTapCount = 1;
      this.lastTouchTapSide = side;
    }
    this.lastTouchTapAt = now;

    clearTimeout(this.touchTapResetTimeout);
    this.touchTapResetTimeout = setTimeout(() => this.resetTouchTapSequence(), this.touchTapCount >= 2 ? 650 : 450);

    if (this.touchTapCount >= 2) {
      event.preventDefault();
      this.seekBy(side === 'backward' ? -10 : 10, (this.touchTapCount - 1) * 10);
    }
  }

  private onSeekPointerCancel(event: PointerEvent) {
    this.touchSeekPointers.delete(event.pointerId);
  }

  private resetTouchTapSequence() {
    clearTimeout(this.touchTapResetTimeout);
    this.touchTapCount = 0;
    this.lastTouchTapAt = 0;
    this.lastTouchTapSide = null;
  }

  // =========================================================
  // PLAYBACK MODIFIERS
  // =========================================================

  private startMomentaryAction(modifier: 'slow', rate: number) {
    const video = this.videoRef().nativeElement;
    this.playbackModifier = modifier;
    this.actionStartTime = Date.now();
    this.wasPlayingBeforeAction = !video.paused;
    video.playbackRate = rate;
    video.play().catch(() => {});
  }

  private endMomentaryAction() {
    const video = this.videoRef().nativeElement;
    const duration = Date.now() - this.actionStartTime;
    video.playbackRate = 1.0;
    this.playbackModifier = 'none';
    if (duration < this.TAP_THRESHOLD_MS) {
      if (this.wasPlayingBeforeAction) video.pause(); else video.play().catch(() => {});
    } else {
      if (!this.wasPlayingBeforeAction) video.pause();
    }
  }

  private startHeldFastForward() {
    const video = this.videoRef().nativeElement;
    this.playbackModifier = 'fast';
    this.wasPlayingBeforeAction = !video.paused;
    video.playbackRate = 2;
    video.play().catch(() => {});
  }

  private endHeldFastForward() {
    const video = this.videoRef().nativeElement;
    video.playbackRate = 1;
    this.playbackModifier = 'none';
    if (!this.wasPlayingBeforeAction) video.pause();
  }

  private startHeldRewind() {
    const video = this.videoRef().nativeElement;
    this.playbackModifier = 'reverse';
    this.wasPlayingBeforeRewind = !video.paused;
    video.pause();
    this.rewindInterval = setInterval(() => {
      const newTime = Math.max(0, video.currentTime - 0.04);
      video.currentTime = newTime;
      this.currentTimeSec.set(newTime);
      this.progressPercentage.set(this.totalDuration() > 0 ? (newTime / this.totalDuration()) * 100 : 0);
      if (newTime <= 0) this.stopRewind();
    }, 40);
  }

  private stopRewind() {
    if (this.rewindInterval) {
      clearInterval(this.rewindInterval);
      this.rewindInterval = null;
    }
  }

  private endHeldRewind() {
    const video = this.videoRef()?.nativeElement;
    this.stopRewind();
    this.playbackModifier = 'none';
    if (this.wasPlayingBeforeRewind && video) video.play().catch(() => {});
  }

  // =========================================================
  // SCRUBBER
  // =========================================================

  onScrubStart() { this.isScrubbing.set(true); }

  onScrubEnd() {
    this.isScrubbing.set(false);
    this.seekBarRef().nativeElement.blur();
  }

  private customScrubbing = false;

  onSliderAreaDown(event: PointerEvent) {
    if (event.target === this.seekBarRef().nativeElement) return;
    if (event.button !== 0) return;
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    this.customScrubbing = true;
    this.isScrubbing.set(true);
    this.seekToClientX(event.clientX);
    this.updateTooltip(event.clientX);
  }

  onSliderAreaMove(event: PointerEvent) {
    this.updateTooltip(event.clientX);
    if (this.customScrubbing) this.seekToClientX(event.clientX);
  }

  onSliderAreaUp(event: PointerEvent) {
    if (!this.customScrubbing) return;
    const target = event.currentTarget as HTMLElement;
    if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId);
    this.customScrubbing = false;
    this.isScrubbing.set(false);
    this.seekBarRef().nativeElement.blur();
  }

  onSliderAreaLeave(_event: PointerEvent) {
    if (!this.customScrubbing && !this.isScrubbing()) this.showPreview.set(false);
  }

  private seekToClientX(clientX: number) {
    const range = this.seekBarRef().nativeElement;
    const rect = range.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, offsetX / rect.width));
    const newTime = percent * this.totalDuration();
    this.videoRef().nativeElement.currentTime = newTime;
    this.progressPercentage.set(percent * 100);
  }

  onScrubberTouchStart(event: TouchEvent) {
    this.isScrubbing.set(true);
    if (event.touches.length > 0) this.updateTooltip(event.touches[0].clientX);
  }

  onScrubberTouch(event: TouchEvent) {
    if (event.touches.length > 0) this.updateTooltip(event.touches[0].clientX);
  }

  onScrubberTouchEnd() {
    this.isScrubbing.set(false);
    this.showPreview.set(false);
    this.seekBarRef().nativeElement.blur();
  }

  private updateTooltip(clientX: number) {
    const range = this.seekBarRef().nativeElement;
    const rect = range.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
    const previewSec = percentage * this.totalDuration();
    this.previewTime.set(this.getRealTimeFromSeconds(previewSec));
    this.previewLeftPx.set(offsetX);
    this.showPreview.set(true);
    this.requestPreviewFrame(previewSec);
  }

  private requestPreviewFrame(previewSec: number) {
    if (!this.previewEnabled()) return;
    const previewVideo = this.previewVideoRef()?.nativeElement;
    if (!previewVideo) return;
    this.pendingPreviewSec = previewSec;
    if (this.previewSeekTimeout) return;
    this.previewSeekTimeout = setTimeout(() => {
      this.previewSeekTimeout = null;
      const sec = this.pendingPreviewSec;
      this.pendingPreviewSec = null;
      if (sec === null) return;
      const v = this.previewVideoRef()?.nativeElement;
      if (v) v.currentTime = sec;
    }, 60);
  }

  togglePreview() {
    const next = !this.previewEnabled();
    this.previewEnabled.set(next);
    this.savePreviewPref(next);
    if (!next) {
      if (this.previewSeekTimeout) {
        clearTimeout(this.previewSeekTimeout);
        this.previewSeekTimeout = null;
      }
      this.pendingPreviewSec = null;
    }
  }

  toggleSettings(event: Event) {
    event.stopPropagation();
    this.showSettings.update((v) => !v);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (this.showSettings() && !target?.closest('.settings-wrapper')) {
      this.showSettings.set(false);
    }
    if (this.showRecordMenu() && !target?.closest('.record-wrapper')) {
      this.showRecordMenu.set(false);
    }
  }

  // =========================================================
  // CONTROLS
  // =========================================================

  togglePlay() {
    const video = this.videoRef().nativeElement;
    if (video.paused) video.play().catch(() => {}); else video.pause();
  }

  toggleMute() {
    const video = this.videoRef().nativeElement;
    if (video.muted || video.volume === 0) {
      video.muted = false;
      if (video.volume === 0) video.volume = 1;
    } else {
      video.muted = true;
      video.volume = 0;
    }
  }

  changeVolume(event: Event) {
    const video = this.videoRef().nativeElement;
    const input = event.target as HTMLInputElement;
    const val = Number(input.value);
    video.volume = val;
    if (val > 0 && video.muted) video.muted = false;
    input.blur();
  }

  seek(event: Event) {
    const video = this.videoRef().nativeElement;
    const input = event.target as HTMLInputElement;
    const percent = Number(input.value);
    video.currentTime = (percent / 100) * this.totalDuration();
  }

  toggleFullscreen() {
    const wrapper = this.playerWrapperRef().nativeElement;
    if (!document.fullscreenElement) wrapper.requestFullscreen().catch(err => console.error(err));
    else document.exitFullscreen();
  }

  // =========================================================
  // RECORDING
  // =========================================================

  onRecordButtonClick(event: Event) {
    event.stopPropagation();
    if (this.isRecording()) {
      void this.stopRecording();
      return;
    }
    this.showRecordMenu.update((v) => !v);
  }

  async startRecording(format: RecordingFormat) {
    // isRecording() only flips true at the END of this method, after an await.
    // A second call arriving in that window (a double-tap on a format button)
    // would slip past it and orphan the first MediaRecorder and its interval,
    // so the re-entrancy guard has to be a synchronous flag.
    if (this.isRecording() || this.recordingStarting) return;
    this.recordingStarting = true;
    try {
      await this.beginRecording(format);
    } finally {
      this.recordingStarting = false;
    }
  }

  private async beginRecording(format: RecordingFormat) {
    this.showRecordMenu.set(false);

    // Applying the format letterboxes the viewport. Everything downstream —
    // baseScale, the clamp effect, the crop rect — keys off viewportW/H, so
    // those must reflect the NEW box before the first frame is captured, or
    // the opening frames get cropped against the old, wider viewport.
    this.recordingFormat.set(format);
    await this.awaitViewportSettle(format);

    const recorder = new FlatRecorder({
      getVideoElement: () => this.videoRef().nativeElement,
      getOverlayCanvas: () => this.canvasRef().nativeElement,
      getCropRect: () => this.computeCropRect(),
    });
    try {
      recorder.start(format, this.fps() ?? 30);
    } catch (err) {
      console.error('Failed to start endzone recording', err);
      this.recordingFormat.set(null);
      return;
    }
    this.recorder = recorder;
    this.recordingStartedAtMs = Date.now();
    this.recordingElapsedSec.set(0);
    this.recordingTickInterval = setInterval(() => {
      this.recordingElapsedSec.set(Math.floor((Date.now() - this.recordingStartedAtMs) / 1000));
    }, 250);

    // Single continuous driver runs through both play and pause states, so
    // pans, zooms and drawings made over a frozen frame are still captured.
    this.startFrameDriver();
    this.isRecording.set(true);
  }

  async stopRecording() {
    if (!this.recorder) {
      this.resetRecordingUI();
      return;
    }
    const recorder = this.recorder;
    const format = this.recordingFormat();
    this.recorder = null;
    this.stopFrameDriver();
    if (this.recordingTickInterval) {
      clearInterval(this.recordingTickInterval);
      this.recordingTickInterval = null;
    }
    this.isRecording.set(false);
    try {
      const result = await recorder.stop();
      if (result && format) {
        recorder.download(result.blob, result.mimeType, format);
      } else {
        // No blob means the encoder never received a usable frame. Say so
        // rather than ending the take with nothing and no explanation.
        console.error('Endzone recording produced no data; nothing to download');
      }
    } catch (err) {
      console.error('Failed to finalize endzone recording', err);
    } finally {
      this.resetRecordingUI();
    }
  }

  private resetRecordingUI() {
    this.recordingFormat.set(null);
    this.recordingElapsedSec.set(0);
    this.recordingStartedAtMs = 0;
  }

  /**
   * Called from destroy. Releases the frame driver and tick interval
   * synchronously, then fire-and-forgets recorder.stop() — the MediaRecorder
   * finishes encoding asynchronously and the resulting blob is discarded
   * because the component is going away.
   */
  private abortRecording() {
    if (!this.recorder) return;
    const recorder = this.recorder;
    this.recorder = null;
    this.stopFrameDriver();
    if (this.recordingTickInterval) {
      clearInterval(this.recordingTickInterval);
      this.recordingTickInterval = null;
    }
    this.isRecording.set(false);
    recorder.stop().catch(() => {});
  }

  /**
   * Block until the viewport element has actually taken the locked aspect
   * ratio. Waiting a fixed number of frames would be a guess: the class has
   * to survive change detection, then layout, then the ResizeObserver
   * callback, and none of those are guaranteed to land in one frame.
   *
   * Rather than trust the observer's timing, this reads the box directly and
   * pushes the signals itself, so the crop math is correct on the very first
   * captured frame. Bounded by a 500 ms deadline so it can never hang.
   */
  private async awaitViewportSettle(format: RecordingFormat): Promise<void> {
    const [w, h] = format.split(':').map(Number);
    const targetRatio = w / h;
    const el = this.videoViewportRef().nativeElement;
    const deadline = performance.now() + 500;
    while (performance.now() < deadline) {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw > 0 && ch > 0 && Math.abs(cw / ch - targetRatio) / targetRatio < 0.02) break;
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
    }
    // Push the measured box straight into the signals rather than waiting for
    // the ResizeObserver to deliver it; the clamp effect then runs on it.
    this.viewportW.set(el.clientWidth);
    this.viewportH.set(el.clientHeight);
    // Same reasoning for the overlay: it must already match the locked box,
    // or the first frames composite a stale-sized stroke layer. Safe to call
    // eagerly — the render loop repaints every stroke each frame anyway.
    this.resizeCanvas();
  }

  /**
   * The region of the source video currently filling the viewport, in source
   * pixels — the inverse of transformStyle(). A source pixel lands on screen
   * at `t + s * (source - natural/2)` relative to the viewport centre, so
   * inverting that for the viewport's own edges gives the crop.
   *
   * Read fresh every frame, which is what makes the recording follow the
   * user's zoom and pan as they adjust them mid-take.
   */
  private computeCropRect(): CropRect | null {
    const vw = this.videoNaturalW();
    const vh = this.videoNaturalH();
    const bw = this.viewportW();
    const bh = this.viewportH();
    if (!vw || !vh || !bw || !bh) return null;
    const s = this.computeBaseScale() * this.zoom();
    if (!(s > 0)) return null;

    // Cover-fit plus clampTranslation() already keep this inside the video,
    // but clamp defensively: an out-of-bounds source rect makes drawImage
    // render transparent edges rather than throw, which would corrupt the
    // output silently.
    const sw = Math.min(bw / s, vw);
    const sh = Math.min(bh / s, vh);
    const sx = Math.max(0, Math.min(vw - sw, vw / 2 + (-bw / 2 - this.tx()) / s));
    const sy = Math.max(0, Math.min(vh - sh, vh / 2 + (-bh / 2 - this.ty()) / s));
    return { sx, sy, sw, sh };
  }

  /**
   * rAF-paced output frame driver. Calls recorder.captureFrame() at exactly
   * the target FPS using schedule-based pacing, so on a 60 Hz display the
   * missed-vs-caught rAFs alternate in a 2-3 pattern (50 ms, 33 ms, 50 ms,
   * 33 ms…) and the average frame rate matches the target over any sustained
   * interval. Elapsed-since-last-push pacing would quantize 40 ms intervals
   * up to 50 ms on a 60 Hz display, yielding 20 fps when 25 was requested.
   */
  private startFrameDriver() {
    this.stopFrameDriver();
    this.recordingFrameDriverActive = true;
    const targetFps = this.fps() ?? 30;
    const targetIntervalMs = 1000 / targetFps;
    let nextPushMs = 0;
    const tick = (now: number) => {
      if (!this.recordingFrameDriverActive) return;
      if (nextPushMs === 0) {
        nextPushMs = now;
      }
      if (now >= nextPushMs) {
        nextPushMs += targetIntervalMs;
        this.recorder?.captureFrame();
      }
      this.recordingRafHandle = requestAnimationFrame(tick);
    };
    this.recordingRafHandle = requestAnimationFrame(tick);
  }

  private stopFrameDriver() {
    this.recordingFrameDriverActive = false;
    if (this.recordingRafHandle !== null) {
      cancelAnimationFrame(this.recordingRafHandle);
      this.recordingRafHandle = null;
    }
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  listenToFullscreenChange() {
    document.addEventListener('fullscreenchange', this.fullscreenHandler);
  }

  trackUserActivity() {
    const reset = () => {
      this.isUserActive.set(true);
      clearTimeout(this.userActivityTimeout);
      this.userActivityTimeout = setTimeout(() => {
        if (this.isPlaying()) this.isUserActive.set(false);
      }, 3000);
    };
    const wrapper = this.playerWrapperRef().nativeElement;
    wrapper.addEventListener('mousemove', reset);
    wrapper.addEventListener('click', reset);
    wrapper.addEventListener('touchstart', reset);
  }

  private getRealTimeFromSeconds(seconds: number): Date {
    const startInput = this.recordingStartTime();
    const timeTracker = new Date(startInput);
    if (!isNaN(timeTracker.getTime())) {
      timeTracker.setSeconds(timeTracker.getSeconds() + seconds);
      return timeTracker;
    }
    return new Date();
  }
}
